import React, { useState } from 'react';
import { Plus, Star, Phone, Mail, DollarSign, Calendar, Trash2, Bell, GripVertical, History, Edit } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import ConfirmModal from '../components/modals/ConfirmModal';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { CrmLead } from '../types';
import { useToast } from '../hooks/useToast';
import { useCrmReminders } from '../hooks/useCrmReminders';
import { NotificationService } from '../utils/notificationService';
import { supabase } from '../lib/supabase';

const CrmSeguimiento: React.FC = () => {
  const { user } = useAuth();
  const {
    addCrmLead,
    updateCrmLead,
    deleteCrmLead,
    addCrmReminder,
    deleteCrmReminder,
    fetchCrmLeads,
    fetchCrmReminders
  } = useAppStore();
  const { toast, ToastComponent } = useToast();
  
  // Usar el hook personalizado para recordatorios (SIN scheduler aquí para evitar duplicados con el global)
  const { crmLeads, crmReminders, isLoading } = useCrmReminders({ enableScheduler: false });
  
  
  
  
  
  
  
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<CrmLead | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState<CrmLead | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [reminderDeleteConfirm, setReminderDeleteConfirm] = useState<string | null>(null);




  const [newLead, setNewLead] = useState({
    nombre: '',
    contacto: '',
    descripcion: '',
    valor_potencial: '',
    calificacion: 1,
    etapa: 'Potencial' as const
  });

  const [newReminder, setNewReminder] = useState(() => {
    const now = new Date();
    const futureTime = new Date(now.getTime() + 5 * 60 * 1000);
    
    // Forzar timezone de Buenos Aires (UTC-3)
    const buenosAiresOffset = -3 * 60;
    const localNow = new Date(now.getTime() + (buenosAiresOffset * 60 * 1000));
    const localFuture = new Date(futureTime.getTime() + (buenosAiresOffset * 60 * 1000));
    
    return {
      titulo: '',
      descripcion: '',
      fecha_recordatorio: localNow.toISOString().split('T')[0], // Fecha de Buenos Aires
      hora_recordatorio: localFuture.toTimeString().slice(0, 5) // 5 minutos en el futuro
    };
  });

  const etapas = [
    { value: 'Potencial', label: 'Potencial', color: 'bg-yellow-100 border-yellow-300 text-yellow-800' },
    { value: 'Seguimiento', label: 'Seguimiento', color: 'bg-blue-100 border-blue-300 text-blue-800' },
    { value: 'Cerrado', label: 'Cerrado', color: 'bg-green-100 border-green-300 text-green-800' },
    { value: 'Perdido', label: 'Perdido', color: 'bg-red-100 border-red-300 text-red-800' }
  ];

  // Los datos se cargan automáticamente a través del hook useCrmReminders

  const handleAddLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      await addCrmLead({
        ...newLead,
        valor_potencial: newLead.valor_potencial ? parseFloat(newLead.valor_potencial) : undefined,
        vendedor_id: user.id
      });

      setNewLead({
        nombre: '',
        contacto: '',
        descripcion: '',
        valor_potencial: '',
        calificacion: 1,
        etapa: 'Potencial'
      });
      setShowAddModal(false);
      toast.success('Lead creado', 'El lead se ha creado correctamente');
    } catch (error) {
      console.error('Error creating lead:', error);
    }
  };

  // Función para actualizar la hora por defecto cuando se abre el modal
  const updateReminderTime = () => {
    const now = new Date();
    const futureTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutos en el futuro
    
    // Forzar timezone de Buenos Aires (UTC-3)
    const buenosAiresOffset = -3 * 60; // -3 horas en minutos
    const localNow = new Date(now.getTime() + (buenosAiresOffset * 60 * 1000));
    const localFuture = new Date(futureTime.getTime() + (buenosAiresOffset * 60 * 1000));
    
    const localDate = localNow.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    const localTime = localFuture.toTimeString().slice(0, 5); // Formato HH:MM
    
    console.log('[REMINDER TIME] Updating reminder time (Buenos Aires):', {
      now: now.toLocaleString('es-AR'),
      futureTime: futureTime.toLocaleString('es-AR'),
      localDate,
      localTime,
      debug: {
        originalDate: now.toISOString(),
        buenosAiresDate: localNow.toISOString(),
        originalTime: futureTime.toISOString(),
        buenosAiresTime: localFuture.toISOString()
      }
    });
    
    setNewReminder(prev => ({
      ...prev,
      fecha_recordatorio: localDate,
      hora_recordatorio: localTime
    }));
  };

  const handleAddReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !selectedLead) return;

    try {
      // Construir la fecha completa usando timezone local
      const fechaCompleta = new Date(`${newReminder.fecha_recordatorio}T${newReminder.hora_recordatorio}:00`);
      
      // Validar que la fecha no sea en el pasado
      const ahora = new Date();
      if (fechaCompleta <= ahora) {
        toast.error('No puedes crear recordatorios en el pasado. Por favor, selecciona una fecha y hora futura.');
        return;
      }
      
      console.log('[LEAD REMINDER] Creating reminder with data:', {
        lead_id: selectedLead.id,
        vendedor_id: user.id,
        titulo: newReminder.titulo,
        descripcion: newReminder.descripcion,
        fecha_recordatorio: fechaCompleta.toISOString(),
        completado: false,
        debug: {
          inputDate: newReminder.fecha_recordatorio,
          inputTime: newReminder.hora_recordatorio,
          constructedDate: fechaCompleta.toString(),
          isoString: fechaCompleta.toISOString(),
          currentTime: new Date().toISOString()
        }
      });
      
      const reminderData = {
        lead_id: selectedLead.id,
        vendedor_id: user.id,
        titulo: newReminder.titulo,
        descripcion: newReminder.descripcion,
        fecha_recordatorio: fechaCompleta.toISOString(),
        completado: false
      };
      
      await addCrmReminder(reminderData);
      
      const fechaFormateada = new Date(`${newReminder.fecha_recordatorio}T${newReminder.hora_recordatorio}:00`).toLocaleString('es-AR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      toast.success(`Recordatorio creado para ${fechaFormateada}`);
      console.log('[LEAD REMINDER] ✅ Reminder created successfully:', reminderData);

      setNewReminder({
        titulo: '',
        descripcion: '',
        fecha_recordatorio: '',
        hora_recordatorio: ''
      });
      setShowReminderModal(false);
      setSelectedLead(null);
      toast.success('Recordatorio creado', 'El recordatorio se ha programado correctamente');
    } catch (error) {
      console.error('[LEAD REMINDER] ❌ Error creating reminder:', error);
      toast.error('Error al crear recordatorio');
    }
  };

  const [draggedLead, setDraggedLead] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    setDraggedLead(leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newEtapa: string) => {
    e.preventDefault();
    
    if (!draggedLead) return;

    try {
      await updateCrmLead(draggedLead, { etapa: newEtapa });
      toast.success('Lead actualizado', 'La etapa del lead se ha actualizado');
    } catch (error) {
      console.error('Error updating lead:', error);
    } finally {
      setDraggedLead(null);
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      await deleteCrmLead(id);
      setDeleteConfirm(null);
      toast.success('Lead eliminado', 'El lead se ha eliminado correctamente');
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const handleEditLead = (lead: CrmLead) => {
    setEditingLead({ ...lead });
    setShowEditModal(true);
  };

  const handleSaveLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLead) return;

    try {
      await updateCrmLead(editingLead.id, {
        nombre: editingLead.nombre,
        descripcion: editingLead.descripcion,
        contacto: editingLead.contacto,
        valor_potencial: editingLead.valor_potencial,
        calificacion: editingLead.calificacion,
        etapa: editingLead.etapa
      });
      
      setShowEditModal(false);
      setEditingLead(null);
      toast.success('Lead actualizado correctamente');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Error al actualizar lead');
    }
  };

  const handleShowHistory = (lead: CrmLead) => {
    setSelectedLead(lead);
    setShowHistoryModal(true);
  };

  const handleDeleteReminder = async (id: string) => {
    try {
      await deleteCrmReminder(id);
      setReminderDeleteConfirm(null);
      toast.success('Recordatorio eliminado', 'El recordatorio se ha eliminado correctamente');
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const getLeadsByEtapa = (etapa: string) => {
    return (crmLeads || []).filter(lead => lead.etapa === etapa);
  };

  const getRemindersForLead = (leadId: string) => {
    return (crmReminders || []).filter(reminder => reminder.lead_id === leadId && !reminder.completado);
  };

  const renderStars = (calificacion: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < calificacion ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Seguimiento de potenciales clientes</h1>
          <p className="text-gray-600 dark:text-gray-400">Gestiona tus leads y oportunidades de venta</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="h-5 w-5" />}
          >
            Nuevo Lead
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {etapas.map(etapa => (
          <div key={etapa.value} className="space-y-4">
            <div className={`p-3 rounded-lg border-2 ${etapa.color}`}>
              <h3 className="font-semibold text-center">{etapa.label}</h3>
              <span className="text-sm opacity-75">
                {getLeadsByEtapa(etapa.value).length} leads
              </span>
            </div>
            
            <div
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, etapa.value)}
              className="min-h-96 p-3 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 transition-colors hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
            >
              {getLeadsByEtapa(etapa.value).map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead.id)}
                  className="mb-3 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border cursor-move hover:shadow-md transition-shadow"
                >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2 flex-1">
                                <GripVertical className="h-4 w-4 text-gray-400" />
                                <h4 className="font-medium text-gray-900 dark:text-white truncate">
                                  {lead.nombre}
                                </h4>
                              </div>
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleShowHistory(lead)}
                                  className="p-1 text-gray-400 hover:text-purple-600"
                                  title="Ver historial"
                                >
                                  <History className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleEditLead(lead)}
                                  className="p-1 text-gray-400 hover:text-green-600"
                                  title="Editar lead"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedLead(lead);
                                    updateReminderTime(); // Actualizar hora antes de abrir modal
                                    setShowReminderModal(true);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Agregar recordatorio"
                                >
                                  <Bell className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(lead.id)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                {/* Badge del creador */}
                {lead.creator && (
                  <div className="mb-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {lead.creator.email}
                    </span>
                  </div>
                )}

                            {lead.contacto && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {lead.contacto.includes('@') ? (
                                  <Mail className="h-4 w-4 mr-1" />
                                ) : (
                                  <Phone className="h-4 w-4 mr-1" />
                                )}
                                <span className="truncate">{lead.contacto}</span>
                              </div>
                            )}

                            {lead.valor_potencial && (
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                                <DollarSign className="h-4 w-4 mr-1" />
                                <span>{formatCurrency(lead.valor_potencial)}</span>
                              </div>
                            )}

                            <div className="flex items-center justify-between mb-2">
                              <div className="flex">{renderStars(lead.calificacion)}</div>
                              <span className="text-xs text-gray-500">
                                {lead.calificacion}/5
                              </span>
                            </div>

                            {lead.descripcion && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {lead.descripcion}
                              </p>
                            )}

                            {/* Recordatorios */}
                            {getRemindersForLead(lead.id).length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Recordatorios:
                                </div>
                                {getRemindersForLead(lead.id).slice(0, 2).map(reminder => (
                                  <div key={reminder.id} className="text-xs text-gray-600 dark:text-gray-400">
                                    • {reminder.titulo} - {formatDateTime(reminder.fecha_recordatorio)}
                                  </div>
                                ))}
                                {getRemindersForLead(lead.id).length > 2 && (
                                  <div className="text-xs text-gray-500">
                                    +{getRemindersForLead(lead.id).length - 2} más
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Nuevo Lead</h3>
            <form onSubmit={handleAddLead} className="space-y-4">
              <Input
                label="Nombre *"
                value={newLead.nombre}
                onChange={(e) => setNewLead({ ...newLead, nombre: e.target.value })}
                required
              />
              <Input
                label="Email o Teléfono"
                value={newLead.contacto}
                onChange={(e) => setNewLead({ ...newLead, contacto: e.target.value })}
                placeholder="ejemplo@email.com o +54 9 11 1234-5678"
              />
              <Input
                label="Valor Potencial (ARS)"
                type="number"
                value={newLead.valor_potencial}
                onChange={(e) => setNewLead({ ...newLead, valor_potencial: e.target.value })}
                placeholder="0"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Calificación (1-5)
                </label>
                <div className="flex space-x-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setNewLead({ ...newLead, calificacion: i + 1 })}
                      className={`p-2 rounded ${
                        i < newLead.calificacion
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Star className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newLead.descripcion}
                  onChange={(e) => setNewLead({ ...newLead, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Crear Lead
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Reminder Modal */}
      {showReminderModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Nuevo Recordatorio - {selectedLead.nombre}
            </h3>
            <form onSubmit={handleAddReminder} className="space-y-4">
              <Input
                label="Título *"
                value={newReminder.titulo}
                onChange={(e) => setNewReminder({ ...newReminder, titulo: e.target.value })}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newReminder.descripcion}
                  onChange={(e) => setNewReminder({ ...newReminder, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fecha *"
                  type="date"
                  value={newReminder.fecha_recordatorio}
                  onChange={(e) => setNewReminder({ ...newReminder, fecha_recordatorio: e.target.value })}
                  required
                />
                <Input
                  label="Hora *"
                  type="time"
                  value={newReminder.hora_recordatorio}
                  onChange={(e) => setNewReminder({ ...newReminder, hora_recordatorio: e.target.value })}
                  required
                />
              </div>
              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Crear Recordatorio
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowReminderModal(false);
                    setSelectedLead(null);
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Lead Modal */}
      {showEditModal && editingLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Editar Lead - {editingLead.nombre}
            </h3>
            <form onSubmit={handleSaveLead} className="space-y-4">
              <Input
                label="Nombre *"
                value={editingLead.nombre}
                onChange={(e) => setEditingLead({ ...editingLead, nombre: e.target.value })}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  value={editingLead.descripcion || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, descripcion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                />
              </div>
              <Input
                label="Contacto"
                value={editingLead.contacto || ''}
                onChange={(e) => setEditingLead({ ...editingLead, contacto: e.target.value })}
                placeholder="Email o teléfono"
              />
              <Input
                label="Valor Potencial"
                type="number"
                value={editingLead.valor_potencial || ''}
                onChange={(e) => setEditingLead({ ...editingLead, valor_potencial: e.target.value })}
                placeholder="0"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Calificación *
                </label>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setEditingLead({ ...editingLead, calificacion: rating })}
                      className={`p-1 rounded ${
                        editingLead.calificacion >= rating
                          ? 'text-yellow-400'
                          : 'text-gray-300 hover:text-yellow-400'
                      }`}
                    >
                      <Star className="h-5 w-5 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Etapa *
                </label>
                <select
                  value={editingLead.etapa}
                  onChange={(e) => setEditingLead({ ...editingLead, etapa: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  {etapas.map((etapa) => (
                    <option key={etapa.value} value={etapa.value}>
                      {etapa.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Guardar Cambios
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingLead(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Historial de Recordatorios - {selectedLead.nombre}
              </h3>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedLead(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              {crmReminders
                .filter(reminder => reminder.lead_id === selectedLead.id)
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((reminder) => (
                  <div
                    key={reminder.id}
                    className={`p-3 rounded-lg border ${
                      reminder.completado
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                        : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {reminder.titulo}
                        </h4>
                        {reminder.descripcion && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {reminder.descripcion}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(reminder.fecha_recordatorio).toLocaleString('es-AR')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            reminder.completado
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          }`}>
                            {reminder.completado ? 'Completado' : 'Pendiente'}
                          </span>
                        </div>
                      </div>
                      {!reminder.completado && (
                        <button
                          onClick={() => setReminderDeleteConfirm(reminder.id)}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Eliminar recordatorio"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              
              {crmReminders.filter(reminder => reminder.lead_id === selectedLead.id).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No hay recordatorios para este lead
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmations */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDeleteLead(deleteConfirm)}
        title="Eliminar Lead"
        message="¿Estás seguro de que quieres eliminar este lead? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={!!reminderDeleteConfirm}
        onClose={() => setReminderDeleteConfirm(null)}
        onConfirm={() => reminderDeleteConfirm && handleDeleteReminder(reminderDeleteConfirm)}
        title="Eliminar Recordatorio"
        message="¿Estás seguro de que quieres eliminar este recordatorio?"
        confirmText="Eliminar"
        variant="danger"
      />

      {ToastComponent}
    </div>
  );
};

export default CrmSeguimiento;
